import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "@/lib/api";

const inputCls =
  "border-0 border-b border-slate-300 shadow-none rounded-none px-0 h-10 font-semibold text-[17px] bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#1F2937] placeholder:text-slate-400";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const [role, setRole] = useState(params.get("role") === "broker" ? "broker" : "borrower");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [referrer, setReferrer] = useState(null);
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!refCode) return;
    axios.get(`${API_BASE}/referral/${refCode}`)
      .then((r) => setReferrer(r.data))
      .catch(() => setReferrer(null));
  }, [refCode]);

  // Any referral link (broker or loan officer) means the invitee is a borrower.
  useEffect(() => {
    if (referrer && role !== "borrower") setRole("borrower");
  }, [referrer, role]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form, role };
      if (refCode) payload.referral_code = refCode;
      await register(payload);
      if (role === "borrower") {
        toast.success(referrer
          ? `Account created — starting your application with ${referrer.name.split(" ")[0]}`
          : "Account created — let's begin your application");
        nav("/app/applications/new");
      } else {
        toast.success("Broker account created — welcome");
        nav("/app");
      }
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="small-caps text-slate-600 text-[11px]">
            Already registered?{" "}
            <Link to="/login" className="text-[#1F2937] underline underline-offset-4 hover:opacity-70" data-testid="reg-signin-link">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-16">
        <div className="small-caps text-slate-500">Free · No impact to your credit at this stage</div>
        <h1 className="mt-6 font-semibold text-[56px] leading-[0.95] tracking-tight text-[#1F2937]">
          Create your <span className="italic">account.</span>
        </h1>
        <p className="mt-6 max-w-lg text-[15.5px] leading-[1.7] text-slate-600">
          {role === "borrower"
            ? "Sixty seconds to set up. Right after, you'll fill in the details of your business and the capital you need."
            : "Set up your Venture Nationals broker account. Once inside you'll be able to submit deals and share your custom application link."}
        </p>

        {referrer && (
          <div
            className="mt-8 border border-slate-300 px-4 py-3 text-[14px] text-slate-700"
            data-testid="reg-referrer-banner"
          >
            You&apos;re applying with{" "}
            <span className="font-semibold text-[#1F2937]">{referrer.company || referrer.name}</span>
            {" "}({referrer.role === "broker" ? "advisor" : "loan officer"}).
          </div>
        )}

        <form onSubmit={submit} className="mt-14 space-y-10">
          <div>
            <div className="small-caps text-slate-500 mb-3">I am a…</div>
            <div className={`grid gap-3 ${referrer ? "grid-cols-1" : "grid-cols-2"}`}>
              {[
                { r: "borrower", t: "Business Owner", d: "I need financing" },
                { r: "broker", t: "Broker / Advisor", d: "I submit deals for clients" },
              ]
                .filter((x) => !(referrer && x.r === "broker"))
                .map((x) => (
                <button
                  key={x.r}
                  type="button"
                  onClick={() => setRole(x.r)}
                  data-testid={`role-select-${x.r}`}
                  className={`text-left border p-5 transition-colors ${
                    role === x.r
                      ? "border-[#1F2937] bg-slate-800/[0.04]"
                      : "border-slate-300 hover:border-[#1F2937]/50"
                  }`}
                >
                  <div className="font-semibold text-[17px] text-[#1F2937]">{x.t}</div>
                  <div className="small-caps text-slate-500 mt-1">{x.d}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="small-caps text-slate-500 mb-1">Full name ·</div>
              <Input
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="reg-name-input"
                className={inputCls}
                placeholder="Jane Q. Founder"
              />
            </div>
            <div>
              <div className="small-caps text-slate-500 mb-1">Email ·</div>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                data-testid="reg-email-input"
                className={inputCls}
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="small-caps text-slate-500 mb-1">Password ·</div>
              <Input
                type="password"
                minLength={6}
                required
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                data-testid="reg-password-input"
                className={inputCls}
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <div className="small-caps text-slate-500 mb-1">Phone</div>
              <Input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                data-testid="reg-phone-input"
                className={inputCls}
                placeholder="(415) 555-0100"
              />
            </div>
          </div>

          {err && (
            <div className="border-l-2 border-red-600 pl-4 py-2 text-[14px] text-red-700" data-testid="reg-error">
              {err}
            </div>
          )}

          <div className="border-t border-slate-200 pt-8 flex items-center justify-between flex-wrap gap-6">
            <div className="max-w-sm">
              <div className="small-caps text-slate-500 mb-1">What happens next</div>
              <div className="text-[13.5px] text-slate-600 leading-[1.6]">
                {role === "borrower"
                  ? "You'll be taken to the full application to describe your business and upload bank statements."
                  : "You'll land on your broker dashboard where you can submit deals and share your application link."}
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="reg-submit-btn"
              className="group inline-flex items-center gap-4 disabled:opacity-40"
            >
              <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
              <span className="small-caps text-[#1F2937]">
                {busy ? "Creating…" : role === "borrower" ? "Continue to application →" : "Create broker account →"}
              </span>
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
